"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
Object.defineProperty(exports, "__esModule", { value: true });
const snap_ui_1 = require("@teamsnap/snap-ui");
const validation_1 = require("core/validation");
const useFeatureFlag_1 = require("hooks/useFeatureFlag");
const React = __importStar(require("react"));
const useAmpli_1 = require("src/hooks/useAmpli");
const useKeboardSubmitHandler_1 = require("src/hooks/useKeboardSubmitHandler");
const timezone_mapping_1 = require("utils/timezone-mapping");
const validation_signup_1 = require("utils/validation-signup");
const layouts_1 = require("../shared/layouts");
const useV1CreateTeam_1 = require("./useV1CreateTeam");
const CreateTeam = () => {
    const { setTeam, trialTeam, errors, setupConfig, submitTeam, isProcessing, isSignup } = (0, useV1CreateTeam_1.useV1CreateTeam)();
    (0, useFeatureFlag_1.useFeatureFlag)('teamNew');
    (0, useKeboardSubmitHandler_1.useKeboardSubmitHandler)(submitTeam);
    const { screenViewed, identify } = (0, useAmpli_1.useAmpli)();
    React.useEffect(() => {
        screenViewed(useAmpli_1.AmplitudeScreenNames.TEAM_CREATE);
        if (!isSignup) {
            identify({ experiment_audience_stripe_team_create: true });
        }
    }, []);
    const teamAgeOptions = [
        {
            label: 'team age',
            options: setupConfig?.ageGroups?.map((age) => ({
                label: age,
                value: age,
            })) || [],
        },
    ];
    const sportOptions = [
        {
            label: 'top sports',
            options: setupConfig?.topSportsList?.map((sport) => ({
                label: sport.name,
                value: sport.id,
            })) || [],
        },
        {
            label: 'all sports',
            options: setupConfig?.sports?.map((sport) => ({
                label: sport.name,
                value: sport.id,
            })) || [],
        },
    ];
    const US_TIMEZONES = new Set([
        'America/New_York',
        'America/Chicago',
        'America/Denver',
        'America/Phoenix',
        'America/Los_Angeles',
        'America/Anchorage',
        'Pacific/Honolulu',
    ]);
    const CA_TIMEZONES = new Set([
        'America/St_Johns',
        'America/Halifax',
        'America/Toronto',
        'America/Winnipeg',
        'America/Regina',
        'America/Edmonton',
        'America/Vancouver',
        'America/Whitehorse',
    ]);
    const allTimezones = setupConfig?.timezones || [];
    const parseOffsetString = (offset) => {
        const match = offset.match(/^([+-])(\d{2}):(\d{2})$/);
        if (!match)
            return 0;
        const [, sign, h, m] = match;
        const total = parseInt(h) * 60 + parseInt(m);
        return sign === '-' ? -total : total;
    };
    const getOffsetMinutes = (ianaName, fallbackOffset) => {
        try {
            const date = new Date('2027-01-01T12:00:00Z');
            const utcStr = date.toLocaleString('en-US', { timeZone: 'UTC' });
            const tzStr = date.toLocaleString('en-US', { timeZone: ianaName });
            return (new Date(tzStr).getTime() - new Date(utcStr).getTime()) / 60000;
        }
        catch {
            return fallbackOffset ? parseOffsetString(fallbackOffset) : 0;
        }
    };
    const getBaseUtcOffset = (ianaName, fallbackOffset) => {
        const totalMinutes = getOffsetMinutes(ianaName, fallbackOffset);
        const sign = totalMinutes >= 0 ? '+' : '-';
        const absMinutes = Math.abs(totalMinutes);
        const hours = String(Math.floor(absMinutes / 60)).padStart(2, '0');
        const minutes = String(absMinutes % 60).padStart(2, '0');
        return `GMT${sign}${hours}:${minutes}`;
    };
    const sortEastToWest = (a, b) => getOffsetMinutes(b.description || 'UTC', b.offset) - getOffsetMinutes(a.description || 'UTC', a.offset);
    const toIanaOption = (tz) => {
        const railsName = (0, timezone_mapping_1.getRailsTimezone)(tz.description || '');
        return {
            value: railsName,
            label: `(${getBaseUtcOffset(tz.description || 'UTC')}) ${tz.description}`,
        };
    };
    const toRailsOption = (tz) => {
        const railsName = (0, timezone_mapping_1.getRailsTimezone)(tz.description || '');
        return {
            value: railsName,
            label: `(${getBaseUtcOffset(tz.description || 'UTC', tz.offset)}) ${railsName}`,
        };
    };
    const timezoneOptions = [
        {
            label: 'United States',
            options: allTimezones
                .filter((tz) => US_TIMEZONES.has(tz.description || ''))
                .sort(sortEastToWest)
                .map(toIanaOption),
        },
        {
            label: 'Canada',
            options: allTimezones
                .filter((tz) => CA_TIMEZONES.has(tz.description || ''))
                .sort(sortEastToWest)
                .map(toIanaOption),
        },
        {
            label: 'Other',
            options: allTimezones
                .filter((tz) => !US_TIMEZONES.has(tz.description || '') && !CA_TIMEZONES.has(tz.description || ''))
                .sort(sortEastToWest)
                .map(toRailsOption),
        },
    ];
    const countryOptions = [
        {
            label: 'choose country',
            options: setupConfig?.topCountriesList?.map((c) => ({
                label: c.name,
                value: c.name,
            })) || [],
        },
        {
            label: '------------------',
            options: setupConfig?.countries?.map((c) => ({
                label: c.name,
                value: c.name,
            })) || [],
        },
    ];
    const Wrapper = isSignup ? layouts_1.SplitWrapper : layouts_1.BasicWrapper;
    const POSTAL_CODE_REGEX_FILTER = trialTeam.country === 'United States' ? validation_1.NON_NUMBERS_REGEX : validation_1.SPECIAL_CHARACTERS_REGEX;
    return (React.createElement(Wrapper, null,
        React.createElement(layouts_1.FormWrapper, null,
            React.createElement("div", { className: "sui-heading-md sui-mb-2" }, "Enter team details"),
            React.createElement("div", null,
                React.createElement(snap_ui_1.Input, { label: "Team name", required: true, helpText: "This can be changed later.", name: "teamName", className: "sui-block sui-mb-2", onChange: (e) => {
                        setTeam('teamName', e.target.value);
                    }, placeholder: "", inputProps: { maxLength: 250 }, value: trialTeam.teamName || '', errors: errors?.teamName ? [errors.teamName] : undefined, type: "text" }),
                React.createElement(snap_ui_1.Select, { label: "Sport", isSearchable: true, required: true, className: "sui-block sui-w-full sui-mb-2", name: "sport", onChange: (e) => {
                        // @ts-ignore
                        setTeam('sport', e.toString());
                    }, value: trialTeam.sport, errors: errors?.sport ? [errors.sport] : undefined, options: sportOptions, placeholder: "Select" }),
                React.createElement(snap_ui_1.Select, { label: "Team age", isSearchable: true, required: true, className: "sui-block sui-w-full sui-mb-2", name: "teamAge", onChange: (e) => {
                        // @ts-ignore
                        setTeam('teamAge', e.toString());
                    }, value: trialTeam.teamAge, errors: errors?.teamAge ? [errors.teamAge] : undefined, options: teamAgeOptions, placeholder: "Select" }),
                React.createElement(snap_ui_1.Select, { className: "sui-mb-2", label: "Country", required: true, isSearchable: true, name: "country", onChange: (e) => {
                        // @ts-ignore
                        setTeam('country', e.toString());
                    }, value: trialTeam.country, errors: errors?.country ? [errors.country] : undefined, options: countryOptions, placeholder: "Select" }),
                (0, validation_signup_1.requirePostalCode)(trialTeam.country) && (React.createElement(snap_ui_1.Input, { label: "Zip/postal code", required: true, name: "postalCode", className: "sui-mb-2", onChange: (e) => {
                        setTeam('postalCode', e.target.value.replace(POSTAL_CODE_REGEX_FILTER, '').toUpperCase());
                    }, placeholder: "", inputProps: {
                        inputMode: trialTeam.country === 'United States' ? 'numeric' : 'text',
                    }, value: trialTeam.postalCode || '', errors: errors?.postalCode ? [errors.postalCode] : undefined, type: "text", autocomplete: "postal-code" })),
                React.createElement(snap_ui_1.Select, { className: "sui-mb-2", label: "Time zone", required: true, name: "timezone", onChange: (e) => {
                        // @ts-ignore
                        setTeam('timezone', e.toString());
                    }, value: trialTeam.timezone, options: timezoneOptions, isSearchable: true, errors: errors?.timezone ? [errors.timezone] : undefined, placeholder: "Select", props: {
                        classNamePrefix: 'sui-react-select__menu--timezone',
                    } }),
                React.createElement(snap_ui_1.LabelButton, { "data-testid": "next-button", className: "sui-min-w-full sui-mt-2", labelText: "Next", onClick: submitTeam, size: "default", variantType: "primary", loading: isProcessing })))));
};
exports.default = CreateTeam;
